import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  Search,
  Pencil,
  Trash2,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Loader2,
} from 'lucide-react'

interface Fund {
  fund_id: number
  name: string
  main_strategy: string | null
  sub_strategy: string | null
  inception_date: string | null
  management_fee: string | null
  incentive_fee: string | null
  liquidity: string | null
  manager_id: number | null
  manager_name: string | null
  return_count: number
  last_return_date: string | null
}

interface FundsResponse {
  funds: Fund[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

interface Dependencies {
  fund_id: number
  fund_name: string
  returns: number
  aum: number
  portfolio: number
}

interface ManagerResult {
  manager_id: number
  name: string
}

export default function FundManagement() {
  const queryClient = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Edit state
  const [editingFund, setEditingFund] = useState<Fund | null>(null)
  const [editForm, setEditForm] = useState({
    name: '',
    manager_id: null as number | null,
    manager_name: '',
    main_strategy: '',
    sub_strategy: '',
    inception_date: '',
    management_fee: '',
    incentive_fee: '',
    liquidity: '',
  })
  const [managerQuery, setManagerQuery] = useState('')
  const [managerResults, setManagerResults] = useState<ManagerResult[]>([])
  const [, setManagerSearching] = useState(false)

  // Delete state
  const [deletingFund, setDeletingFund] = useState<Fund | null>(null)
  const [deps, setDeps] = useState<Dependencies | null>(null)
  const [depsLoading, setDepsLoading] = useState(false)
  const [cascadeDelete, setCascadeDelete] = useState(false)

  // Create state
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', manager_id: null as number | null, strategy: '' })
  const [createManagerQuery, setCreateManagerQuery] = useState('')
  const [createManagerResults, setCreateManagerResults] = useState<ManagerResult[]>([])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Fund list query
  const { data, isLoading } = useQuery<FundsResponse>({
    queryKey: ['fund-management', searchQuery, page],
    queryFn: () =>
      api.get('/fund-management/funds', { params: { q: searchQuery, page, page_size: 50 } }).then((r) => r.data),
    staleTime: 30_000,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (vars: { fund_id: number; data: Record<string, unknown> }) =>
      api.put(`/fund-management/funds/${vars.fund_id}`, vars.data).then((r) => r.data),
    onSuccess: () => {
      showMessage('success', 'Fund updated successfully')
      setEditingFund(null)
      queryClient.invalidateQueries({ queryKey: ['fund-management'] })
    },
    onError: (err: any) => showMessage('error', err.response?.data?.detail || 'Update failed'),
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (vars: { fund_id: number; cascade: boolean }) =>
      api.delete(`/fund-management/funds/${vars.fund_id}`, { params: { cascade: vars.cascade } }).then((r) => r.data),
    onSuccess: (result) => {
      const parts = ['Fund deleted']
      if (result.cascade_deleted?.returns > 0) parts.push(`${result.cascade_deleted.returns} returns removed`)
      if (result.cascade_deleted?.aum > 0) parts.push(`${result.cascade_deleted.aum} AUM records removed`)
      showMessage('success', parts.join(', '))
      setDeletingFund(null)
      setDeps(null)
      setCascadeDelete(false)
      queryClient.invalidateQueries({ queryKey: ['fund-management'] })
    },
    onError: (err: any) => showMessage('error', err.response?.data?.detail || 'Delete failed'),
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: { name: string; manager_id: number | null; strategy: string }) =>
      api.post('/fund-upload/create-fund', data).then((r) => r.data),
    onSuccess: (result) => {
      showMessage('success', `Fund "${result.name}" created (ID: ${result.fund_id})`)
      setShowCreate(false)
      setCreateForm({ name: '', manager_id: null, strategy: '' })
      setCreateManagerQuery('')
      queryClient.invalidateQueries({ queryKey: ['fund-management'] })
    },
    onError: (err: any) => showMessage('error', err.response?.data?.detail || 'Create failed'),
  })

  function showMessage(type: 'success' | 'error', text: string) {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 6000)
  }

  // Open edit modal
  function startEdit(fund: Fund) {
    setEditingFund(fund)
    setEditForm({
      name: fund.name,
      manager_id: fund.manager_id,
      manager_name: fund.manager_name || '',
      main_strategy: fund.main_strategy || '',
      sub_strategy: fund.sub_strategy || '',
      inception_date: fund.inception_date || '',
      management_fee: fund.management_fee || '',
      incentive_fee: fund.incentive_fee || '',
      liquidity: fund.liquidity || '',
    })
    setManagerQuery('')
    setManagerResults([])
  }

  function saveEdit() {
    if (!editingFund) return
    const payload: Record<string, unknown> = {}
    if (editForm.name !== editingFund.name) payload.name = editForm.name
    if (editForm.manager_id !== editingFund.manager_id) payload.manager_id = editForm.manager_id
    if (editForm.main_strategy !== (editingFund.main_strategy || '')) payload.main_strategy = editForm.main_strategy
    if (editForm.sub_strategy !== (editingFund.sub_strategy || '')) payload.sub_strategy = editForm.sub_strategy
    if (editForm.inception_date !== (editingFund.inception_date || '')) payload.inception_date = editForm.inception_date || null
    if (editForm.management_fee !== (editingFund.management_fee || '')) payload.management_fee = editForm.management_fee
    if (editForm.incentive_fee !== (editingFund.incentive_fee || '')) payload.incentive_fee = editForm.incentive_fee
    if (editForm.liquidity !== (editingFund.liquidity || '')) payload.liquidity = editForm.liquidity

    if (Object.keys(payload).length === 0) {
      setEditingFund(null)
      return
    }
    updateMutation.mutate({ fund_id: editingFund.fund_id, data: payload })
  }

  // Open delete dialog
  async function startDelete(fund: Fund) {
    setDeletingFund(fund)
    setCascadeDelete(false)
    setDepsLoading(true)
    try {
      const r = await api.get(`/fund-management/funds/${fund.fund_id}/dependencies`)
      setDeps(r.data)
    } catch {
      setDeps({ fund_id: fund.fund_id, fund_name: fund.name, returns: 0, aum: 0, portfolio: 0 })
    }
    setDepsLoading(false)
  }

  // Manager search for edit
  useEffect(() => {
    if (!managerQuery || managerQuery.length < 2) {
      setManagerResults([])
      return
    }
    setManagerSearching(true)
    const timer = setTimeout(() => {
      api.get('/fund-upload/search-managers', { params: { q: managerQuery } })
        .then((r) => setManagerResults(r.data))
        .catch(() => setManagerResults([]))
        .finally(() => setManagerSearching(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [managerQuery])

  // Manager search for create
  useEffect(() => {
    if (!createManagerQuery || createManagerQuery.length < 2) {
      setCreateManagerResults([])
      return
    }
    const timer = setTimeout(() => {
      api.get('/fund-upload/search-managers', { params: { q: createManagerQuery } })
        .then((r) => setCreateManagerResults(r.data))
        .catch(() => setCreateManagerResults([]))
    }, 300)
    return () => clearTimeout(timer)
  }, [createManagerQuery])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fund Management</h1>
          <p className="text-muted-foreground">Create, edit, and delete funds in the alternatives database</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Fund
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Fund list */}
      <div className="bg-card border border-border rounded-lg">
        {/* Search */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by fund name or manager..."
                className="w-full h-10 pl-9 pr-3 border border-input rounded-md bg-background text-sm"
              />
            </div>
            {data && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {data.total} funds
              </span>
            )}
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : data && data.funds.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fund Name</th>
                    <th>Manager</th>
                    <th>Strategy</th>
                    <th className="text-right">Returns</th>
                    <th>Last Return</th>
                    <th className="w-28">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.funds.map((fund) => (
                    <tr key={fund.fund_id} className="hover:bg-muted/50">
                      <td>
                        <div className="font-medium">{fund.name}</div>
                        <div className="text-xs text-muted-foreground">ID: {fund.fund_id}</div>
                      </td>
                      <td className="text-sm">{fund.manager_name || '-'}</td>
                      <td className="text-sm">{fund.main_strategy || '-'}</td>
                      <td className="text-right font-mono text-sm">{fund.return_count}</td>
                      <td className="text-sm text-muted-foreground">{fund.last_return_date || '-'}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEdit(fund)}
                            className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted"
                            title="Edit fund"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => startDelete(fund)}
                            className="h-7 w-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600"
                            title="Delete fund"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <span className="text-sm text-muted-foreground">
                  Page {data.page} of {data.total_pages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="h-8 px-3 border border-border rounded text-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" /> Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                    disabled={page === data.total_pages}
                    className="h-8 px-3 border border-border rounded text-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Next <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No funds found.
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingFund && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEditingFund(null)}>
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-semibold">Edit Fund</h2>
              <button onClick={() => setEditingFund(null)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              {/* Name */}
              <div>
                <label className="text-xs text-muted-foreground">Fund Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                />
              </div>

              {/* Manager */}
              <div>
                <label className="text-xs text-muted-foreground">Manager</label>
                <div className="text-sm mb-1">
                  {editForm.manager_name ? (
                    <span className="inline-flex items-center gap-1 bg-muted px-2 py-0.5 rounded text-xs">
                      {editForm.manager_name}
                      <button onClick={() => setEditForm({ ...editForm, manager_id: null, manager_name: '' })}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs">No manager</span>
                  )}
                </div>
                <input
                  type="text"
                  value={managerQuery}
                  onChange={(e) => setManagerQuery(e.target.value)}
                  placeholder="Search managers..."
                  className="w-full h-8 px-3 border border-input rounded-md bg-background text-sm"
                />
                {managerResults.length > 0 && (
                  <div className="border border-border rounded-md mt-1 max-h-32 overflow-y-auto">
                    {managerResults.map((m) => (
                      <button
                        key={m.manager_id}
                        onClick={() => {
                          setEditForm({ ...editForm, manager_id: m.manager_id, manager_name: m.name })
                          setManagerQuery('')
                          setManagerResults([])
                        }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                      >
                        {m.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Strategy */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Main Strategy</label>
                  <input
                    type="text"
                    value={editForm.main_strategy}
                    onChange={(e) => setEditForm({ ...editForm, main_strategy: e.target.value })}
                    className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Sub Strategy</label>
                  <input
                    type="text"
                    value={editForm.sub_strategy}
                    onChange={(e) => setEditForm({ ...editForm, sub_strategy: e.target.value })}
                    className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  />
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Inception Date</label>
                  <input
                    type="date"
                    value={editForm.inception_date}
                    onChange={(e) => setEditForm({ ...editForm, inception_date: e.target.value })}
                    className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Liquidity</label>
                  <input
                    type="text"
                    value={editForm.liquidity}
                    onChange={(e) => setEditForm({ ...editForm, liquidity: e.target.value })}
                    className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Management Fee</label>
                  <input
                    type="text"
                    value={editForm.management_fee}
                    onChange={(e) => setEditForm({ ...editForm, management_fee: e.target.value })}
                    className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Incentive Fee</label>
                  <input
                    type="text"
                    value={editForm.incentive_fee}
                    onChange={(e) => setEditForm({ ...editForm, incentive_fee: e.target.value })}
                    className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <button
                onClick={() => setEditingFund(null)}
                className="h-9 px-4 border border-border rounded-md text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={updateMutation.isPending || !editForm.name.trim()}
                className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingFund && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setDeletingFund(null); setDeps(null) }}>
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 p-4 border-b border-border">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <h2 className="font-semibold">Delete Fund</h2>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm">
                Are you sure you want to delete <strong>{deletingFund.name}</strong>?
              </p>

              {depsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking dependencies...
                </div>
              ) : deps && (
                <>
                  {deps.portfolio > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                      This fund is in {deps.portfolio} portfolio position(s). Remove it from the portfolio before deleting.
                    </div>
                  )}
                  {(deps.returns > 0 || deps.aum > 0) && deps.portfolio === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-700 space-y-2">
                      <p>This fund has associated data:</p>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {deps.returns > 0 && <li>{deps.returns.toLocaleString()} return records</li>}
                        {deps.aum > 0 && <li>{deps.aum.toLocaleString()} AUM records</li>}
                      </ul>
                      <label className="flex items-center gap-2 mt-2">
                        <input
                          type="checkbox"
                          checked={cascadeDelete}
                          onChange={(e) => setCascadeDelete(e.target.checked)}
                          className="rounded"
                        />
                        <span>Also delete all associated data</span>
                      </label>
                    </div>
                  )}
                  {deps.returns === 0 && deps.aum === 0 && deps.portfolio === 0 && (
                    <p className="text-sm text-muted-foreground">This fund has no associated data and can be safely deleted.</p>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <button
                onClick={() => { setDeletingFund(null); setDeps(null) }}
                className="h-9 px-4 border border-border rounded-md text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate({ fund_id: deletingFund.fund_id, cascade: cascadeDelete })}
                disabled={
                  deleteMutation.isPending ||
                  depsLoading ||
                  (deps !== null && deps.portfolio > 0) ||
                  (deps !== null && (deps.returns > 0 || deps.aum > 0) && !cascadeDelete)
                }
                className="h-9 px-4 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete Fund
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Fund Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCreate(false)}>
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-semibold">Create Fund</h2>
              <button onClick={() => setShowCreate(false)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Fund Name *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Enter fund name"
                  className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Manager</label>
                {createForm.manager_id ? (
                  <div className="flex items-center gap-1 mb-1">
                    <span className="inline-flex items-center gap-1 bg-muted px-2 py-0.5 rounded text-xs">
                      {createManagerQuery || 'Selected'}
                      <button onClick={() => { setCreateForm({ ...createForm, manager_id: null }); setCreateManagerQuery('') }}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={createManagerQuery}
                    onChange={(e) => setCreateManagerQuery(e.target.value)}
                    placeholder="Search managers..."
                    className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                  />
                )}
                {createManagerResults.length > 0 && !createForm.manager_id && (
                  <div className="border border-border rounded-md mt-1 max-h-32 overflow-y-auto">
                    {createManagerResults.map((m) => (
                      <button
                        key={m.manager_id}
                        onClick={() => {
                          setCreateForm({ ...createForm, manager_id: m.manager_id })
                          setCreateManagerQuery(m.name)
                          setCreateManagerResults([])
                        }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                      >
                        {m.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Strategy</label>
                <input
                  type="text"
                  value={createForm.strategy}
                  onChange={(e) => setCreateForm({ ...createForm, strategy: e.target.value })}
                  placeholder="e.g. Equity Long/Short"
                  className="w-full h-9 px-3 border border-input rounded-md bg-background text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <button
                onClick={() => setShowCreate(false)}
                className="h-9 px-4 border border-border rounded-md text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => createMutation.mutate(createForm)}
                disabled={createMutation.isPending || !createForm.name.trim()}
                className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Fund
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
